import { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
  Globe,
  Building2,
  PauseCircle,
  PlayCircle,
  Loader2,
  ExternalLink,
  Zap,
  ArrowRight
} from "lucide-react";

export default function Competitors() {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompetitor, setEditingCompetitor] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    facebook_url: "",
    linkedin_url: "",
    google_ads_domain: "",
    sector: "",
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  const loadCompetitors = useCallback(async () => {
    try {
      const response = await apiClient.get("/competitors");
      setCompetitors(response.data);
    } catch (error) {
      toast.error("Error al cargar competidores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitors();
  }, [loadCompetitors]);

  const resetForm = () => {
    setFormData({
      name: "",
      facebook_url: "",
      linkedin_url: "",
      google_ads_domain: "",
      sector: "",
      notes: ""
    });
    setEditingCompetitor(null);
  };

  const openDialog = (competitor = null) => {
    if (competitor) {
      setEditingCompetitor(competitor);
      setFormData({
        name: competitor.name,
        facebook_url: competitor.facebook_url || "",
        linkedin_url: competitor.linkedin_url || "",
        google_ads_domain: competitor.google_ads_domain || "",
        sector: competitor.sector,
        notes: competitor.notes || ""
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingCompetitor) {
        await apiClient.put(`/competitors/${editingCompetitor.id}`, formData);
        toast.success("Competidor actualizado");
      } else {
        await apiClient.post("/competitors", formData);
        toast.success("Competidor agregado · Ve al Dashboard para lanzar tu análisis", {
          action: {
            label: "Ir al Dashboard",
            onClick: () => { window.location.href = "/"; }
          },
          duration: 8000
        });
      }
      closeDialog();
      loadCompetitors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este competidor?")) return;

    try {
      await apiClient.delete(`/competitors/${id}`);
      toast.success("Competidor eliminado");
      loadCompetitors();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggle = async (id) => {
    try {
      const response = await apiClient.patch(`/competitors/${id}/toggle`);
      toast.success(response.data.active ? "Competidor activado" : "Competidor pausado");
      loadCompetitors();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="competitors-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Competidores
          </h1>
          <p className="text-slate-400 mt-1">
            Gestiona los competidores a monitorear
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={() => openDialog()}
              className="bg-cyan-600 hover:bg-cyan-500 text-white btn-glow"
              data-testid="add-competitor-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Competidor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">
                {editingCompetitor ? "Editar Competidor" : "Nuevo Competidor"}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {editingCompetitor 
                  ? "Modifica los datos del competidor" 
                  : "Agrega un nuevo competidor para monitorear sus anuncios"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-slate-300">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Nike, Adidas..."
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                  required
                  data-testid="competitor-name-input"
                />
              </div>
              <div>
                <Label htmlFor="sector" className="text-slate-300">Sector/Industria *</Label>
                <Input
                  id="sector"
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  placeholder="Ej: E-commerce, SaaS, Fitness..."
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50"
                  required
                  data-testid="competitor-sector-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label htmlFor="facebook_url" className="text-slate-300">URL de Facebook Ads Library</Label>
                  <a
                    href="https://www.facebook.com/ads/library"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                    data-testid="meta-library-link"
                  >
                    Búscalo aquí <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Input
                  id="facebook_url"
                  value={formData.facebook_url}
                  onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                  placeholder="https://www.facebook.com/ads/library/?..."
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="competitor-facebook-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label htmlFor="linkedin_url" className="text-slate-300">URL o nombre en LinkedIn Ads Library (opcional)</Label>
                  <a
                    href="https://www.linkedin.com/ad-library/home"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                    data-testid="linkedin-library-link"
                  >
                    Búscalo aquí <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Input
                  id="linkedin_url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                  placeholder="Nombre del anunciante en LinkedIn Ads Library"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="competitor-linkedin-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Label htmlFor="google_ads_domain" className="text-slate-300">Dominio o nombre en Google Ads Transparency (opcional)</Label>
                  <a
                    href="https://adstransparency.google.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"
                    data-testid="google-library-link"
                  >
                    Búscalo aquí <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
                <Input
                  id="google_ads_domain"
                  value={formData.google_ads_domain}
                  onChange={(e) => setFormData({ ...formData, google_ads_domain: e.target.value })}
                  placeholder="ejemplo.com o nombre del anunciante"
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 font-mono text-sm"
                  data-testid="competitor-google-ads-input"
                />
              </div>
              <div>
                <Label htmlFor="notes" className="text-slate-300">Notas (opcional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionales sobre este competidor..."
                  className="mt-1.5 bg-slate-950/50 border-white/10 text-white placeholder:text-slate-600 focus:border-cyan-500/50 min-h-[80px]"
                  data-testid="competitor-notes-input"
                />
              </div>
              <DialogFooter className="gap-2 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeDialog}
                  className="border-white/10 text-slate-300 hover:bg-white/5"
                  data-testid="cancel-competitor-btn"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-cyan-600 hover:bg-cyan-500 text-white"
                  data-testid="save-competitor-btn"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {editingCompetitor ? "Guardar Cambios" : "Agregar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Competitors Grid */}
      {competitors.length === 0 ? (
        <div className="glass rounded-xl p-12">
          <div className="empty-state">
            <Users className="empty-state-icon" />
            <p className="empty-state-title">Sin competidores</p>
            <p className="empty-state-description">
              Agrega tu primer competidor para comenzar a analizar sus anuncios
            </p>
            <Button
              onClick={() => openDialog()}
              className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white btn-glow"
              data-testid="add-first-competitor-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Agregar Competidor
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Next-step banner */}
          <div className="mb-6 flex items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/20" data-testid="next-step-banner">
            <div className="w-10 h-10 rounded-lg brand-gradient flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium">
                Listo · Ya puedes lanzar tu análisis
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                Ve al Dashboard y elige los competidores activos para empezar.
              </p>
            </div>
            <RouterLink
              to="/"
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium transition-colors"
              data-testid="go-to-dashboard-btn"
            >
              Lanzar análisis <ArrowRight className="w-4 h-4" />
            </RouterLink>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {competitors.map((competitor) => (
              <CompetitorCard
                key={competitor.id}
                competitor={competitor}
                onEdit={() => openDialog(competitor)}
                onDelete={() => handleDelete(competitor.id)}
                onToggle={() => handleToggle(competitor.id)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CompetitorCard({ competitor, onEdit, onDelete, onToggle }) {
  return (
    <div 
      className={`glass rounded-xl p-6 card-hover ${!competitor.active ? "opacity-60" : ""}`}
      data-testid={`competitor-card-${competitor.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            competitor.active ? "bg-cyan-500/10 text-cyan-400" : "bg-slate-800 text-slate-500"
          }`}>
            <Building2 className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{competitor.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              competitor.active 
                ? "bg-emerald-500/10 text-emerald-400" 
                : "bg-slate-700/50 text-slate-500"
            }`}>
              {competitor.active ? "Activo" : "Pausado"}
            </span>
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-slate-400 hover:text-white"
              data-testid={`competitor-menu-${competitor.id}`}
            >
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-slate-900 border-white/10">
            <DropdownMenuItem 
              onClick={onEdit}
              className="text-slate-300 focus:text-white focus:bg-white/5"
              data-testid={`edit-competitor-${competitor.id}`}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={onToggle}
              className="text-slate-300 focus:text-white focus:bg-white/5"
              data-testid={`toggle-competitor-${competitor.id}`}
            >
              {competitor.active ? (
                <>
                  <PauseCircle className="w-4 h-4 mr-2" />
                  Pausar
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4 mr-2" />
                  Activar
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={onDelete}
              className="text-red-400 focus:text-red-300 focus:bg-red-950/30"
              data-testid={`delete-competitor-${competitor.id}`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-slate-400">
          <Globe className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{competitor.sector}</span>
        </div>
        {competitor.facebook_url && (
          <div className="flex items-center gap-2 text-slate-500">
            <span className="w-4 h-4 flex-shrink-0 text-center text-xs font-bold">M</span>
            <span className="truncate font-mono text-xs">{competitor.facebook_url.slice(0, 40)}...</span>
          </div>
        )}
        {competitor.linkedin_url && (
          <div className="flex items-center gap-2 text-slate-500">
            <span className="w-4 h-4 flex-shrink-0 text-center text-xs font-bold">L</span>
            <span className="truncate font-mono text-xs">{competitor.linkedin_url.slice(0, 40)}...</span>
          </div>
        )}
        {competitor.google_ads_domain && (
          <div className="flex items-center gap-2 text-slate-500">
            <span className="w-4 h-4 flex-shrink-0 text-center text-xs font-bold">G</span>
            <span className="truncate font-mono text-xs">{competitor.google_ads_domain.slice(0, 40)}...</span>
          </div>
        )}
        {competitor.notes && (
          <p className="text-slate-500 text-xs mt-3 line-clamp-2">{competitor.notes}</p>
        )}
      </div>
    </div>
  );
}
